import { Fragment, useEffect, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import Button from './buttons/button';
import Alert from './alert';
import FormInput from './forms/input';
import { required, smsKey } from './forms/validations';
import Segment from './segment';
import Divider from './divider';
import { useFormErrorMessage } from '../../hooks/index/use-form-error-message';
import { APIErrorType } from '../../api';

export type SMSKeyFormPropsType = {
  showTitle?: boolean;
  back?: boolean;
  mobile: string;
  onSubmit: (smsKey: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack?: () => void;
  onError: (error: string) => void;
};

function SMSKeyForm({
  showTitle = true,
  back = false,
  mobile,
  onSubmit,
  onResend,
  onBack = () => {},
  onError
}: SMSKeyFormPropsType) {
  const DEFAULT_TIME_LEFT = 60;
  const [timeLeft, setTimeLeft] = useState(DEFAULT_TIME_LEFT);
  const { formErrorMessage, setFormErrorMessage, resetFormErrorMessage } = useFormErrorMessage();

  useEffect(function () {
    const timer = setTimeout(function () {
      setTimeLeft(timeLeft => (timeLeft > 0 ? timeLeft - 1 : 0));
    }, 1000);
    return function () {
      clearInterval(timer);
    };
  });

  async function submit({ smsKey }: { smsKey: string }) {
    try {
      await onSubmit(smsKey);
    } catch (error) {
      const apiError = error as APIErrorType;
      if (apiError.status === 403) {
        onError(apiError.data.detail || apiError.data);
      } else {
        setFormErrorMessage(apiError.data.smsKey);
      }
    }
  }

  async function resend() {
    try {
      await onResend();
      setTimeLeft(DEFAULT_TIME_LEFT);
    } catch (error) {
      const apiError = error as APIErrorType;
      onError(apiError.data.detail || apiError.data);
    }
  }

  return (
    <Segment>
      {showTitle && <Divider content='فرم ارسال کد احراز هویت' />}

      <div className='text-large text-center'>
        <p className='my-6 mt-8'>
          <span>شماره تلفن همراه: </span>
          {mobile}
        </p>
        {back && (
          <Button
            outline
            content='تغییر شماره تلفن همراه'
            onClick={onBack}
            className='mx-auto my-4'
          />
        )}
      </div>

      <hr />
      <div className='my-4'>
        <p className='my-4 text-center'>
          یک کد 6 رقمی برای شما ارسال شد، لطفا کد ارسال شده را وارد نمایید.
        </p>

        <Formik initialValues={{ smsKey: '' }} onSubmit={submit}>
          {({ dirty, isSubmitting }) => (
            <Fragment>
              {dirty && formErrorMessage && (
                <Alert content={formErrorMessage} className='mt-3 mb-5' />
              )}
              {/* form */}
              <Form onChange={resetFormErrorMessage}>
                <Field
                  required
                  name='smsKey'
                  label='کد احراز هویت'
                  placeholder='کد ارسال شده به شماره همراه'
                  component={FormInput}
                  maxLength={6}
                  validate={smsKey}
                />
                <Button
                  color='blue'
                  type='submit'
                  disabled={!dirty || isSubmitting}
                  content='ادامه'
                  className='my-2 mr-auto'
                />
              </Form>
            </Fragment>
          )}
        </Formik>
      </div>

      <hr />

      <div className='text-large mt-4 text-center'>
        {timeLeft > 0 ? (
          `زمان انتظار برای ارسال مجدد: ${timeLeft} ثانیه`
        ) : (
          <Button
            outline
            color='yellow'
            disabled={timeLeft > 0}
            content='ارسال مجدد کد'
            onClick={resend}
            className='mx-auto'
          />
        )}
      </div>
    </Segment>
  );
}

export default SMSKeyForm;
